import axios from 'axios'
import {
  getToken,
  setToken,
  removeToken,
  getRefreshToken,
  setRefreshToken,
  removeRefreshToken
} from '@/utils/tokenStorage'
// import { getEnvVariable } from '@/utils/helpers'

const API_URL = process.env.VUE_APP_API_URL || 'https://localhost/api/'
// const API_URL = getEnvVariable(
//   'VUE_APP_API_URL',
//   'https://hc-api2.widomin.com/'
// )
const AXIOS_WITH_CREDENTIALS = true
// console.log('Raw API_URL from env:', process.env.VUE_APP_API_URL)
// console.log('Processed API_URL:', API_URL)
axios.defaults.withCredentials = AXIOS_WITH_CREDENTIALS

const instance = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  withCredentials: AXIOS_WITH_CREDENTIALS, // Important for sending cookies/sessions
  headers: {
    'Content-Type': 'application/json',
    Accept: '/',
    // Accept: '*/*',
    'Access-Control-Allow-Credentials': 'true'
    // Accept: 'application/json'
  },
  // In development, simply ignore the certificate validation
  validateStatus: function (status) {
    return status >= 200 && status < 300 // default
  }
})

// Contador global para los intentos de refresh
let refreshAttempts = 0
const MAX_REFRESH_ATTEMPTS = 10

instance.interceptors.request.use(
  (config) => {
    const token = getToken()

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    // Ensure consistency in URLs
    if (config.url && config.url.includes('localhost')) {
      config.url = config.url.replace('localhost', '127.0.0.1')
    }

    config.withCredentials = AXIOS_WITH_CREDENTIALS

    // Handle different types of content
    if (config.data instanceof FormData) {
      config.headers['Content-Type'] = 'multipart/form-data'
    }

    return config
  },
  (error) => {
    console.error('Request Error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.log('error', error)
    const originalRequest = error.config

    const handleLogoutAndRedirect = () => {
      console.log('handleLogoutAndRedirect')
      removeToken()
      removeRefreshToken()

      // Dar tiempo para que se muestre el mensaje de error
      setTimeout(() => {
        window.location.href = '/login'
      }, 3000) // 3 segundos de delay
    }

    // Si excedimos el número máximo de intentos, logout
    if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
      refreshAttempts = 0 // Reset counter
      handleLogoutAndRedirect()
      return Promise.reject(error)
    }

    // Si recibimos un 401 y no hemos intentado refresh, intentar refresh
    if (error.response && error.response.status === 401) {
      refreshAttempts++ // Incrementar contador
      try {
        const refresh = getRefreshToken()
        const headers = {
          Authorization: `Bearer ${refresh}`,
          'Content-Type': 'application/json'
        }
        removeToken()

        const response = await instance.post(
          `${API_URL}/auth/refresh-token`,
          {},
          {
            headers: headers,
            withCredentials: AXIOS_WITH_CREDENTIALS
          }
        )

        // Cambiar de access_token a token para coincidir con la respuesta de la API
        if (response.data.token) {
          setToken(response.data.token)
          if (response.data.refresh_token) {
            setRefreshToken(response.data.refresh_token)
          }
          // Retry the original request with the new token
          originalRequest.headers['Authorization'] =
            `Bearer ${response.data.token}`
          return instance(originalRequest)
        }
      } catch (refreshError) {
        refreshAttempts = 0 // Reset counter on error
        handleLogoutAndRedirect()
        return Promise.reject(refreshError)
      }
    }

    // Si es un error 422 u otro error, simplemente rechazar
    return Promise.reject(error)
  }
)

// Helper to get headers with the token
const getHeaders = (customHeaders = {}) => {
  const token = getToken()
  return {
    Authorization: token ? `Bearer ${token}` : '',
    ...customHeaders
  }
}

// Helper methods for different types of requests
export const api = {
  get: async (url, config = {}) => {
    // console.log('HEADERS:', {
    //   ...config,
    //   headers: {
    //     ...getHeaders(config.headers)
    //   },
    //   withCredentials: AXIOS_WITH_CREDENTIALS
    // })
    return instance.get(url, {
      ...config,
      headers: {
        ...getHeaders(config.headers)
      },
      withCredentials: AXIOS_WITH_CREDENTIALS
    })
  },

  post: async (url, data = {}, config = {}) => {
    return instance.post(url, data, {
      ...config,
      headers: {
        ...getHeaders(config.headers)
      },
      withCredentials: AXIOS_WITH_CREDENTIALS
    })
  },

  put: async (url, data = {}, config = {}) => {
    return instance.put(url, data, {
      ...config,
      headers: {
        ...getHeaders(config.headers)
      },
      withCredentials: AXIOS_WITH_CREDENTIALS
    })
  },

  delete: async (url, config = {}) => {
    return instance.delete(url, {
      ...config,
      headers: {
        ...getHeaders(config.headers)
      },
      withCredentials: AXIOS_WITH_CREDENTIALS
    })
  },

  // Specific method for file upload
  upload: async (url, formData, onUploadProgress = () => {}) => {
    return instance.post(url, formData, {
      headers: {
        ...getHeaders(),
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress,
      withCredentials: AXIOS_WITH_CREDENTIALS
    })
  },

  // Method for logout that cleans tokens
  logout: async () => {
    try {
      await instance.delete('/auth/logout', {
        headers: getHeaders(),
        withCredentials: AXIOS_WITH_CREDENTIALS
      })
      removeToken()
      removeRefreshToken()
      return true
    } catch (error) {
      console.error('Error during logout:', error)
      return false
    }
  },

  // // Method for token refresh
  // refreshToken: async () => {
  //   try {
  //     console.log('ENTRO EN refreshToken')
  //     const refresh = getRefreshToken()
  //     if (!refresh) throw new Error('No refresh token available')

  //     const response = await instance.post(
  //       '/auth/refresh-token',
  //       {},
  //       {
  //         headers: {
  //           Authorization: `Bearer ${refresh}`,
  //           'Content-Type': 'application/json'
  //         },
  //         withCredentials: AXIOS_WITH_CREDENTIALS
  //       }
  //     )

  //     // Cambiar de access_token a token
  //     if (response.data.token) {
  //       setToken(response.data.token)
  //       if (response.data.refresh_token) {
  //         setRefreshToken(response.data.refresh_token)
  //       }
  //       return response.data
  //     }
  //   } catch (error) {
  //     console.error('Error refreshing token:', error)
  //     removeToken()
  //     removeRefreshToken()
  //     throw error
  //   }
  // },

  // Method to check the current session
  checkSession: async () => {
    try {
      const response = await instance.get('/auth/check-session', {
        headers: getHeaders(),
        withCredentials: AXIOS_WITH_CREDENTIALS
      })
      return response.data
    } catch (error) {
      console.error('Session check failed:', error)
      throw error
    }
  },

  // Specific method for login
  login: async (credentials) => {
    try {
      const response = await instance.post('/auth/login', credentials, {
        withCredentials: AXIOS_WITH_CREDENTIALS
      })

      // Save tokens if they are in the response
      if (response.data?.token) {
        setToken(response.data.token)
      }
      if (response.data?.refresh_token) {
        setRefreshToken(response.data.refresh_token)
      }

      return response.data
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }
}

// Export the default instance
export default instance
